import { Injectable } from '@angular/core';

import { Observable, firstValueFrom } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { StatusState } from '@dg/shared-rsm';

import {
  AssignmentType,
  AssignmentsAPI,
  AssignmentsResponse,
  AssignmentsSortBy,
  AssignmentsSortDir,
  AssignmentsState,
  AssignmentsViewModel,
} from '@app/profile/components/profile-assignments/profile-assignments-reactive-store/profile-assignments.model';
import { ProfileAssignmentService } from '@app/profile/components/profile-assignments/profile-assignments.service';
import { ProfileAssignmentsStore } from './profile-assignments.store';

import { HttpErrorResponse } from '@angular/common/http';

import {
  getDaysDifference,
  mergeOverrides,
  stateToAssignmentOptions,
} from './utils';

import { delayAtLeast, readFirst } from '@dg/shared-rxjs';
import { NotifierService, WebEnvironmentService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { EvaluationService } from '@app/tags/services/evaluation.service';
import { IncompleteTagRatingsPipe } from '@app/tags/pipes/incomplete-tag-ratings.pipe';
import { NormalizedRecommendationEntity } from '@app/recommendations/recommendations.api';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TagRatingTrackerService } from '@app/tags/services/tag-rating-tracker.service';
import { TagRatingsForTypePipe } from '@app/tags/pipes/tag-ratings-for-type.pipe';
import { TagRatingService } from '@app/tags/services/tag-rating.service';

/**
 * Facade for the new Profile Assignments page
 */
@Injectable()
export class ProfileAssignmentsFacade {
  public status$: Observable<StatusState>;
  public vm$: Observable<AssignmentsViewModel>;

  public i18n = this.translateService.instant([
    'dgAssignmentsOverview_NoAssignmentsHeader',
    'dgAssignmentsOverview_EmptyToDoText',
    'dgAssignmentsOverview_EmptyCompletedTitle',
    'dgAssignmentsOverview_EmptyCompletedText',
    'dgAssignmentsOverview_NoCompletedAssignments',
    'ProfileCtrl_NoExactMatches',
    'ProfileCtrl_NoExactMatchesHelp',
  ]);

  constructor(
    private store: ProfileAssignmentsStore,
    private api: ProfileAssignmentService,
    private translateService: TranslateService,
    private webEnvironmentService: WebEnvironmentService,
    private evaluationService: EvaluationService,
    public incompleteTagRatingsPipe: IncompleteTagRatingsPipe,
    private notifierService: NotifierService,
    private translate: TranslateService,
    private tagRatingTrackerService: TagRatingTrackerService,
    private ratingsForTypePipe: TagRatingsForTypePipe,
    private tagRatingService: TagRatingService
  ) {
    this.vm$ = this.store.state$.pipe(
      map(this.addViewModelAPI.bind(this)),
      map(this.addComputedProperties.bind(this)),
      shareReplay<AssignmentsViewModel>({ refCount: true })
    );
    this.status$ = this.store.status$;
  }

  public get snapshot(): AssignmentsState {
    return this.store.snapshot;
  }

  public searchAssignments(
    updatedAssignmentsSearch: Partial<AssignmentsState> = {},
    resetStore = true
  ): Observable<boolean> {
    return new Observable((subscriber) => {
      const state = this.store.getState(
        mergeOverrides(updatedAssignmentsSearch)
      );
      const next = (response: AssignmentsResponse) => {
        this.store.updateAssignments(response, state, resetStore);
        this.prefetchPage(response.pagination.currentPage + 1);
        subscriber.next(true);
        subscriber.complete();
      };
      const error = (err: HttpErrorResponse) => {
        this.store.handleError({});
        subscriber.error(err);
      };

      this.api
        .loadAssignmentsByStatus(stateToAssignmentOptions(state))
        .pipe(this.store.trackLoadStatus(), delayAtLeast(450))
        .subscribe({ next, error });
    });
  }

  public refreshAssignments() {
    readFirst(this.searchAssignments(this.store.getState(), true));
  }

  public showPage(page = 1): Promise<boolean> {
    const inRange = this.store.pageInRange(page);
    let result = Promise.resolve(false);

    if (inRange) {
      const fromCache = this.store.selectPage(page);
      if (fromCache) {
        if (!this.store.hasPage(page + 1)) {
          this.prefetchPage(page + 1);
          result = Promise.resolve(true);
        }
      } else {
        const pagination = { currentPage: page };
        result = firstValueFrom(this.searchAssignments({ pagination }, false));
      }
    }

    return result;
  }

  /**
   * Update current assignment type
   */
  public updateAssignmentType(
    assignmentType: AssignmentType
  ): Promise<boolean> {
    const state = this.resetPagination({
      ...this.store.getState(),
      assignmentType,
    });
    return firstValueFrom(this.searchAssignments(state));
  }

  /**
   * Update current assignment search
   */
  public updateAssignmentSearchTerm(searchTerm: string): Promise<boolean> {
    const state = this.resetPagination({
      ...this.store.getState(),
      term: searchTerm,
    });

    return firstValueFrom(this.searchAssignments(state));
  }

  /**
   * Update current sorting
   */
  public updateSort(sortBy: string): Promise<boolean> {
    const sort = {
      sortBy: sortBy as AssignmentsSortBy,
      sortDir: 'asc' as AssignmentsSortDir,
    };
    const currentSortDir = this.snapshot.sort.sortDir;
    if (this.snapshot.sort.sortBy === sortBy) {
      sort.sortDir = (
        currentSortDir === 'desc' ? 'asc' : 'desc'
      ) as AssignmentsSortDir;
    }

    const state = this.resetPagination({
      ...this.store.getState(),
      sort,
    });
    return firstValueFrom(this.searchAssignments(state));
  }

  public getNumberOfDaysFromDueDate(dateDue: string): number {
    const days = dateDue
      ? getDaysDifference(new Date(dateDue), new Date())
      : undefined;
    return days;
  }

  public getNumberOfDaysString(dateDue: string, isOverdue: boolean): string {
    const formattedDate = dateDue.replace(/-/g, '/');
    const time = formattedDate
      ? getDaysDifference(new Date(formattedDate), new Date())
      : undefined;

    let daysString;

    if (time === 0) {
      daysString = 'Core_Today';
    } else if (isOverdue && time === 1) {
      daysString = 'Core_1DayAgoFormat';
    } else if (isOverdue) {
      daysString = 'Core_DaysAgoFormat';
    } else if (time === 1) {
      daysString = 'Core_In1DayFormat';
    } else {
      daysString = 'Core_InDaysFormat';
    }

    return this.translateService.instant(daysString, { time });
  }

  public goToSkillRating(
    event: MouseEvent,
    item: NormalizedRecommendationEntity
  ) {
    const tag = item.reference as unknown as TagsApi.TagDetails;
    switch (item.action) {
      case InternalTagRatingTypes.credential:
        // Skill Certifications are no longer allowed, however we may need to handle previously recommended Skill Certs
        return this.notifierService.showError(
          this.translate.instant('dgTagRating_InvalidRequest')
        );
      case InternalTagRatingTypes.evaluation:
        const allEvaluationRatings = this.ratingsForTypePipe.transform(
          tag.ratings as TagsApi.UserTagRatingDetails[],
          'Evaluation'
        );
        const incompleteRatings =
          this.incompleteTagRatingsPipe.transform(allEvaluationRatings);
        if (!allEvaluationRatings.length) {
          this.evaluationService.startEvaluation(
            tag,
            event.target as HTMLElement
          );
        } else if (incompleteRatings.length) {
          this.tagRatingTrackerService.trackRatingUpdateStepCompleted(
            event.target as HTMLElement,
            tag,
            InternalTagRatingTypes.evaluation,
            'assignments-table-eval-button',
            'Continue'
          );
          this.evaluationService.continueEvaluation(tag.name, tag.tagId);
        } else {
          this.evaluationService.reviewEvaluationResults(tag.name, tag.tagId);
        }
        break;
      case InternalTagRatingTypes.self:
        const selfRating = tag.ratings.find((r) => r.type === 'Self');
        const level = selfRating?.level;
        this.tagRatingService
          .openSelfRatingModal(event, {
            ...tag,
            rating: { level: level } as unknown as TagsApi.UserTagRating,
          })
          .subscribe((rating: TagsApi.UserTagRating) => {
            selfRating.level = rating.level;
          });
        break;
    }
  }

  /**
   * Inject the Facade API into view model
   */
  private addViewModelAPI(
    state: AssignmentsState
  ): AssignmentsState & AssignmentsAPI {
    const api = {
      updateAssignmentType: this.updateAssignmentType.bind(this),
      updateSort: this.updateSort.bind(this),
      getNumberOfDaysString: this.getNumberOfDaysString.bind(this),
      updateAssignmentSearchTerm: this.updateAssignmentSearchTerm.bind(this),
    };

    // Override default showPage with custom functionality to prefetch next page
    state.pagination.showPage = this.showPage.bind(this);

    return {
      ...state,
      ...api,
    };
  }

  private resetPagination(state: AssignmentsState): AssignmentsState {
    const pagination = {
      ...state.pagination,
      currentPage: 1,
    };
    return {
      ...state,
      pagination,
    };
  }

  private prefetchPage(page: number): void {
    if (this.store.pageInRange(page) && !this.store.hasPage(page)) {
      const state = this.store.getState((draft: AssignmentsState) => {
        draft.pagination.currentPage = page;
      });
      const request$ = this.api.loadAssignmentsByStatus(
        stateToAssignmentOptions(state)
      );

      request$.subscribe(({ assignments }: AssignmentsResponse) => {
        this.store.addPage(assignments, page, false);
      });
    }
  }

  /**
   * Inject the Facade computed properties into view model
   */
  private addComputedProperties(state): AssignmentsState {
    const { assignmentType: type } = state;

    const hasAssignments = !!state.results.length;
    const isSearching = state.term && state.term !== '';
    const showComplete = type === 'complete';
    const showIncomplete = !showComplete;

    const computed = {
      emptyStateTitle: showComplete
        ? this.i18n.dgAssignmentsOverview_NoCompletedAssignments
        : this.i18n.dgAssignmentsOverview_NoAssignmentsHeader,
      emptyStateSearchTitle: this.i18n.ProfileCtrl_NoExactMatches,
      emptyStateText: showComplete
        ? this.i18n.dgAssignmentsOverview_EmptyCompletedText
        : this.i18n.dgAssignmentsOverview_EmptyToDoText,
      emptyStateSearchText: this.i18n.ProfileCtrl_NoExactMatchesHelp,
      emptyStateImage: hasAssignments
        ? ''
        : this.webEnvironmentService.getBlobUrl(
            '/content/img/emptystate/profile-settings.svg',
            true
          ),
      showIncompleteTab: showIncomplete && hasAssignments,
      showCompleteTab: showComplete && hasAssignments,
      hasAssignments,
      isSearching,
    };

    return {
      ...state,
      ...computed,
    };
  }
}
